import React from 'react';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useUser } from '@/contexts/UserContext';
import { 
  Sparkles, MessageCircle, ShoppingBag, Activity, User, Settings, Wallet, 
  Building2, Users, Briefcase, DollarSign, BarChart3, HelpCircle,
  Lightbulb, BookOpen, Navigation, MessageSquare, CheckCircle, ArrowRight,
  Play, Target, TrendingUp, Shield, Bell
} from 'lucide-react';

const HelpPage = () => {
  const { accountType } = useUser();

  return (
    <Layout>
      <PageHeader 
        title="Help Center." 
        subtitle="Find everything you need to navigate TheSlice effectively"
      />
      <div className="container mx-auto px-4 py-6 max-w-[1200px]">
        <Tabs defaultValue="getting-started" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 h-auto gap-2 bg-transparent p-0">
            <TabsTrigger value="getting-started" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-xl py-2.5">
              <Play className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Get Started</span>
              <span className="sm:hidden">Start</span>
            </TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-xl py-2.5">
              <BookOpen className="h-4 w-4 mr-2" />
              Features
            </TabsTrigger>
            <TabsTrigger value="navigation" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-xl py-2.5">
              <Navigation className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Navigation</span>
              <span className="sm:hidden">Nav</span>
            </TabsTrigger>
            <TabsTrigger value="tips" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-xl py-2.5">
              <Lightbulb className="h-4 w-4 mr-2" />
              Tips
            </TabsTrigger>
            <TabsTrigger value="faq" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-xl py-2.5">
              <MessageSquare className="h-4 w-4 mr-2" />
              FAQ
            </TabsTrigger>
          </TabsList>

          {/* Getting Started Tab */}
          <TabsContent value="getting-started" className="space-y-6">
            <Card className="border-none shadow-apple-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Welcome to TheSlice
                </CardTitle>
                <CardDescription>
                  Your platform for athlete deals, partnerships, and portfolio management
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="p-4 rounded-xl bg-muted/50 space-y-2">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-500" />
                      For Talent & Agents
                    </h3>
                    <ul className="text-sm text-muted-foreground space-y-1.5">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                        Browse and apply to deals in the Marketplace
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                        Track deal progress and deliverables
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                        Manage your holdings and wallet
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                        Use SliceAI for deal analysis and recommendations
                      </li>
                    </ul>
                  </div>
                  <div className="p-4 rounded-xl bg-muted/50 space-y-2">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-purple-500" />
                      For Brands & Ventures
                    </h3>
                    <ul className="text-sm text-muted-foreground space-y-1.5">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                        Create and manage deals with talent
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                        Discover athletes in the Talent Marketplace
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                        Track campaign performance and payments
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                        Build your organization with team members
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-accent border border-primary/20">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    Quick Start Tip
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Start by completing your profile to unlock all features. Then explore SliceAI - your AI-powered assistant for deal insights and recommendations.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Features Guide Tab */}
          <TabsContent value="features" className="space-y-4">
            <Accordion type="single" collapsible className="space-y-2">
              <AccordionItem value="sliceai" className="border rounded-xl px-4 shadow-apple-sm">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary">
                      <Sparkles className="h-4 w-4 text-primary-foreground" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">SliceAI</div>
                      <div className="text-sm text-muted-foreground">Your AI-powered assistant</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4">
                  <div className="space-y-3 text-sm">
                    <p>SliceAI helps you navigate deals, get recommendations, and analyze opportunities.</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        Ask questions about deals and get instant analysis
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        Get personalized recommendations based on your profile
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        Generate deal summaries and insights
                      </li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="signal" className="border rounded-xl px-4 shadow-apple-sm">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary">
                      <MessageCircle className="h-4 w-4 text-primary-foreground" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">Signal / Feed</div>
                      <div className="text-sm text-muted-foreground">Social activity and analytics</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4">
                  <div className="space-y-3 text-sm">
                    <p>Stay connected with the community and track your social performance.</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        View and create posts in the feed
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        Access the Analytics Vault for social media insights
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        Track engagement metrics and growth
                      </li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="marketplace" className="border rounded-xl px-4 shadow-apple-sm">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary">
                      <ShoppingBag className="h-4 w-4 text-primary-foreground" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">Marketplace</div>
                      <div className="text-sm text-muted-foreground">Browse and apply to deals</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4">
                  <div className="space-y-3 text-sm">
                    <p>Discover available deals from brands and ventures.</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        Filter deals by category, value, and type
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        View deal details and eligibility criteria
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        Apply to deals with one click
                      </li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="deals" className="border rounded-xl px-4 shadow-apple-sm">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary">
                      <Activity className="h-4 w-4 text-primary-foreground" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">Deal Progress</div>
                      <div className="text-sm text-muted-foreground">Track your active deals</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4">
                  <div className="space-y-3 text-sm">
                    <p>Monitor and manage your ongoing deals.</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        View deal timeline and milestones
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        Track deliverables and tasks
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        Communicate with deal partners
                      </li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="wallet" className="border rounded-xl px-4 shadow-apple-sm">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary">
                      <DollarSign className="h-4 w-4 text-primary-foreground" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">Wallet</div>
                      <div className="text-sm text-muted-foreground">Manage your funds</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4">
                  <div className="space-y-3 text-sm">
                    <p>Handle deposits, withdrawals, and payment methods.</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        View your balance and transaction history
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        Link bank accounts and payout methods
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        Process deposits and withdrawals
                      </li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {(accountType === 'talent' || accountType === 'agent') && (
                <AccordionItem value="holdings" className="border rounded-xl px-4 shadow-apple-sm">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary">
                        <Wallet className="h-4 w-4 text-primary-foreground" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold">Holdings</div>
                        <div className="text-sm text-muted-foreground">Your portfolio overview</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-4">
                    <div className="space-y-3 text-sm">
                      <p>Track your investments and equity holdings.</p>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                          View all your holdings in one place
                        </li>
                        <li className="flex items-start gap-2">
                          <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                          Track performance metrics
                        </li>
                        <li className="flex items-start gap-2">
                          <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                          Add new holdings and update values
                        </li>
                      </ul>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {(accountType === 'brand' || accountType === 'venture') && (
                <AccordionItem value="talent-marketplace" className="border rounded-xl px-4 shadow-apple-sm">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary">
                        <Users className="h-4 w-4 text-primary-foreground" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold">Talent Marketplace</div>
                        <div className="text-sm text-muted-foreground">Discover athletes</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-4">
                    <div className="space-y-3 text-sm">
                      <p>Find and connect with athletes across 12+ sports.</p>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                          Browse featured talent on the platform
                        </li>
                        <li className="flex items-start gap-2">
                          <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                          Access the verified athlete database
                        </li>
                        <li className="flex items-start gap-2">
                          <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                          Filter by sport, team, and claim status
                        </li>
                      </ul>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              <AccordionItem value="profile" className="border rounded-xl px-4 shadow-apple-sm">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary">
                      <User className="h-4 w-4 text-primary-foreground" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">Profile</div>
                      <div className="text-sm text-muted-foreground">Manage your presence</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4">
                  <div className="space-y-3 text-sm">
                    <p>Customize your profile and control your visibility.</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        Edit your bio, avatar, and details
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        View your talent rating and tier
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-primary mt-0.5" />
                        Manage account-specific settings
                      </li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>

          {/* Navigation Tips Tab */}
          <TabsContent value="navigation" className="space-y-4">
            <Card className="border-none shadow-apple-sm">
              <CardHeader>
                <CardTitle>Navigating TheSlice</CardTitle>
                <CardDescription>Quick guide to getting around the platform</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Navigation className="h-4 w-4 text-primary" />
                    Main Navigation
                  </h3>
                  <div className="grid gap-3">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                      <Sparkles className="h-5 w-5 text-primary" />
                      <div>
                        <div className="font-medium">SliceAI</div>
                        <div className="text-sm text-muted-foreground">AI assistant for deals and insights</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                      <MessageCircle className="h-5 w-5 text-primary" />
                      <div>
                        <div className="font-medium">Signal</div>
                        <div className="text-sm text-muted-foreground">Social feed and analytics vault</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                      <ShoppingBag className="h-5 w-5 text-primary" />
                      <div>
                        <div className="font-medium">Marketplace</div>
                        <div className="text-sm text-muted-foreground">Browse available deals</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                      <Activity className="h-5 w-5 text-primary" />
                      <div>
                        <div className="font-medium">Deal Progress</div>
                        <div className="text-sm text-muted-foreground">Track active deals</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Settings className="h-4 w-4 text-primary" />
                    Top Bar Icons
                  </h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                      <DollarSign className="h-5 w-5" />
                      <span className="text-sm">Wallet & Payments</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                      <User className="h-5 w-5" />
                      <span className="text-sm">Your Profile</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                      <Settings className="h-5 w-5" />
                      <span className="text-sm">Settings</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                      <HelpCircle className="h-5 w-5" />
                      <span className="text-sm">Help (You are here!)</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tips Tab */}
          <TabsContent value="tips" className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Card className="border-none shadow-apple-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-yellow-500" />
                    Pro Tips
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/50">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div className="text-sm">
                      <div className="font-medium">Complete Your Profile</div>
                      <div className="text-muted-foreground">A complete profile improves your talent rating and visibility</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/50">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div className="text-sm">
                      <div className="font-medium">Use SliceAI Daily</div>
                      <div className="text-muted-foreground">Get personalized deal recommendations and market insights</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/50">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div className="text-sm">
                      <div className="font-medium">Check the Marketplace Regularly</div>
                      <div className="text-muted-foreground">New deals are posted frequently - stay ahead of the competition</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-apple-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-500" />
                    Best Practices
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/50">
                    <Bell className="h-5 w-5 text-primary mt-0.5" />
                    <div className="text-sm">
                      <div className="font-medium">Enable Notifications</div>
                      <div className="text-muted-foreground">Stay updated on deal status and messages</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/50">
                    <TrendingUp className="h-5 w-5 text-primary mt-0.5" />
                    <div className="text-sm">
                      <div className="font-medium">Track Your Analytics</div>
                      <div className="text-muted-foreground">Connect social accounts to the Analytics Vault</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/50">
                    <MessageSquare className="h-5 w-5 text-primary mt-0.5" />
                    <div className="text-sm">
                      <div className="font-medium">Respond Promptly</div>
                      <div className="text-muted-foreground">Quick responses improve your reputation</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq" className="space-y-4">
            <Accordion type="single" collapsible className="space-y-2">
              <AccordionItem value="faq-1" className="border rounded-xl px-4 shadow-apple-sm">
                <AccordionTrigger className="hover:no-underline text-left">
                  How do I apply to a deal?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Navigate to the Marketplace, browse available deals, click on one to view details, and click the "Apply" button. You can add a note to strengthen your application.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-2" className="border rounded-xl px-4 shadow-apple-sm">
                <AccordionTrigger className="hover:no-underline text-left">
                  How is my talent rating calculated?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Your talent rating is based on four categories: Athletic Performance (35%), Social Influence (25%), Business Value (25%), and Profile Quality (15%). Each category has specific metrics that contribute to your overall score.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-3" className="border rounded-xl px-4 shadow-apple-sm">
                <AccordionTrigger className="hover:no-underline text-left">
                  How do I withdraw funds from my wallet?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Go to your Wallet, ensure you have a linked payout method (bank account or similar), then click "Withdraw" and follow the prompts. Processing times vary by method.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-4" className="border rounded-xl px-4 shadow-apple-sm">
                <AccordionTrigger className="hover:no-underline text-left">
                  What is SliceAI and how do I use it?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  SliceAI is your AI-powered assistant. Access it from the main navigation. You can ask questions about deals, get recommendations, analyze market trends, and receive personalized insights.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-5" className="border rounded-xl px-4 shadow-apple-sm">
                <AccordionTrigger className="hover:no-underline text-left">
                  How do I connect my social media accounts?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Go to Signal → Analytics tab to access the Analytics Vault. From there, you can connect Instagram, TikTok, YouTube, and other platforms to track your social metrics.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-6" className="border rounded-xl px-4 shadow-apple-sm">
                <AccordionTrigger className="hover:no-underline text-left">
                  How do I send feedback or report an issue?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Click the feedback icon (chat bubble with plus) in the top navigation bar. You can report bugs, suggest features, or provide general feedback. We review all submissions.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default HelpPage;
